import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { BUSINESS } from "@/lib/config";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <main className="pt-28 pb-16 md:pt-36 md:pb-24" data-testid="privacy-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-[#00296B] font-semibold text-sm hover:text-[#FFD500] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>

          <div className="text-xs uppercase tracking-[0.3em] text-[#334155] font-semibold mb-4">
            — Legal
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#00296B] leading-tight mm-underline">
            Privacy Policy
          </h1>
          <p className="mt-6 text-sm text-[#334155]">
            Last updated: {new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}
          </p>

          <div className="mt-12 space-y-10 text-[#334155] leading-relaxed">
            <Section title="1. Introduction">
              <p>
                At <strong>{BUSINESS.name}</strong> ("we", "us", "our"), we
                respect your privacy and are committed to protecting the
                personal information you share with us. This Privacy Policy
                explains how we collect, use and safeguard the information you
                provide through our website and WhatsApp enquiries.
              </p>
            </Section>

            <Section title="2. Information We Collect">
              <p>When you contact us through our enquiry form, WhatsApp, phone or email, we may collect:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Your name</li>
                <li>Mobile number</li>
                <li>Email address (if provided)</li>
                <li>Product / service interest and any message you send us</li>
              </ul>
              <p className="mt-3">
                We do not automatically collect personal information from
                casual visitors and we do not use tracking cookies for
                advertising.
              </p>
            </Section>

            <Section title="3. How We Use Your Information">
              <p>The information you share is used only to:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Respond to your enquiry about our electrical products</li>
                <li>Provide quotes, product details and availability</li>
                <li>Follow up on your order or query</li>
                <li>Improve our customer service</li>
              </ul>
            </Section>

            <Section title="4. WhatsApp Communication">
              <p>
                Our contact form and product enquiry buttons open WhatsApp with
                pre-filled text. When you send that message, standard WhatsApp
                privacy terms apply. We store your message only within our
                WhatsApp Business account for the purpose of servicing your
                enquiry.
              </p>
            </Section>

            <Section title="5. Sharing of Information">
              <p>
                We <strong>do not sell, rent or share</strong> your personal
                information with third parties for marketing purposes. Your
                data is only accessed by authorised staff of {BUSINESS.name}{" "}
                for handling your enquiry.
              </p>
            </Section>

            <Section title="6. Data Security">
              <p>
                We take reasonable measures to protect the information you
                share with us. However, no method of transmission over the
                internet is 100% secure and we cannot guarantee absolute
                security.
              </p>
            </Section>

            <Section title="7. Your Rights">
              <p>
                You may at any time request us to update or delete the personal
                information we hold about you by writing to us at{" "}
                <a
                  href={`mailto:${BUSINESS.email}`}
                  className="text-[#00296B] font-semibold underline hover:text-[#FFD500]"
                >
                  {BUSINESS.email}
                </a>{" "}
                or calling{" "}
                <a
                  href={`tel:${BUSINESS.phoneRaw}`}
                  className="text-[#00296B] font-semibold underline hover:text-[#FFD500]"
                >
                  {BUSINESS.phoneDisplay}
                </a>
                .
              </p>
            </Section>

            <Section title="8. Third-Party Links">
              <p>
                Our website may include links to third-party sites (e.g., brand
                partners, Google Maps, WhatsApp, Instagram). We are not
                responsible for the privacy practices of those websites.
              </p>
            </Section>

            <Section title="9. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. Any
                changes will be posted on this page with an updated "Last
                updated" date.
              </p>
            </Section>

            <Section title="10. Contact Us">
              <p>
                For any questions regarding this Privacy Policy, please
                contact:
              </p>
              <div className="mt-4 p-6 bg-[#F8F9FA] border border-[#00296B]/10">
                <p className="font-semibold text-[#00296B]">{BUSINESS.name}</p>
                <p className="mt-1 text-sm">
                  {BUSINESS.address.line1} {BUSINESS.address.line2}{" "}
                  {BUSINESS.address.line3}
                </p>
                <p className="mt-2 text-sm">
                  Phone:{" "}
                  <a
                    href={`tel:${BUSINESS.phoneRaw}`}
                    className="text-[#00296B] font-semibold underline"
                  >
                    {BUSINESS.phoneDisplay}
                  </a>
                </p>
                <p className="text-sm">
                  Email:{" "}
                  <a
                    href={`mailto:${BUSINESS.email}`}
                    className="text-[#00296B] font-semibold underline"
                  >
                    {BUSINESS.email}
                  </a>
                </p>
              </div>
            </Section>
          </div>
        </div>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#00296B] leading-tight mb-4">
        {title}
      </h2>
      <div className="text-base sm:text-lg space-y-3">{children}</div>
    </section>
  );
}
